import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import CustomPagination from 'components/CustomPagination/CustomPagination';
import { useGetAiChatDatasourcePageQuery } from 'store/slices/aiChats/apis/aiChatsApi';

import { DATASOURCE_PAGE_SIZE } from '../constants/datasource';
import formatDatasourceCell, { isNumericPgType } from '../utils/formatDatasourceCell';
import readDatasourceDroppedReason from '../utils/readDatasourceDroppedReason';
import AiChatDatasourceEmpty from './AiChatDatasourceEmpty';

import type { ReactElement } from 'react';
import type IAiChatDatasourceGrid from '../interfaces/IAiChatDatasourceGrid';

import styles from '../sass/AiChat.module.scss';

/**
 * The rows of a stored result, paged from the datasource route.
 *
 * The model never sees these rows: the reply carries the columns and the count, and the grid
 * reads the cells from here, on a live turn and on a chat loaded from history alike. Rows travel
 * positionally, so a cell is read by the place of its column in the page, not by a key. The
 * columns shown may be a subset of the page's, in their own order, so each is first located in
 * the page by name — once per page, not per cell.
 *
 * Rows the route cannot make again (a 422: dropped, and the statement no longer runs) leave the
 * route's reason in place of the grid. The page is not asked for again.
 */
function AiChatDatasourceGrid({ datasourceId, columns, rowCount }: IAiChatDatasourceGrid): ReactElement {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);

  const totalPages = Math.max(1, Math.ceil(rowCount / DATASOURCE_PAGE_SIZE));

  const {
    data, error, isFetching, isError,
  } = useGetAiChatDatasourcePageQuery({
    datasourceId,
    offset: (page - 1) * DATASOURCE_PAGE_SIZE,
    limit: DATASOURCE_PAGE_SIZE,
  });

  const droppedReason = readDatasourceDroppedReason(error);

  // Where each shown column sits in a page row; -1 for a column the page does not carry.
  const cellIndexes = useMemo(
    () => columns.map((column) => (data?.columns ?? []).findIndex(({ name }) => name === column.name)),
    [columns, data?.columns],
  );

  const readCell = (row: unknown[], columnIndex: number): unknown => {
    const cellIndex = cellIndexes[columnIndex];

    return cellIndex === -1 ? null : row[cellIndex];
  };

  if (droppedReason !== null) {
    return <AiChatDatasourceEmpty reason={droppedReason} />;
  }

  return (
    <>
      <div className={clsx(styles.tableScroll, styles.datasourceScroll)}>
        <table className={clsx(styles.table, styles.datasourceTable)}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.name}
                  className={clsx({ [styles.datasourceNumber]: isNumericPgType(column.pgType) })}
                >
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((row, rowIndex) => (
              // Rows have no key of their own; their place in the page is what tells them apart.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${data?.offset}-${rowIndex}`}>
                {columns.map((column, columnIndex) => (
                  <td
                    key={column.name}
                    className={clsx({ [styles.datasourceNumber]: isNumericPgType(column.pgType) })}
                  >
                    {formatDatasourceCell(readCell(row, columnIndex), column.pgType)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isFetching ? <p className={styles.note}>{t('aiChat.tools.datasourceLoading')}</p> : null}
      {isError ? <p className={styles.error}>{t('aiChat.tools.datasourceLoadFailed')}</p> : null}
      {data && !data.rows.length && !isFetching ? (
        <p className={styles.note}>{t('aiChat.tools.datasourceEmpty')}</p>
      ) : null}

      <CustomPagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        className={styles.datasourcePagination}
      />
    </>
  );
}

export default AiChatDatasourceGrid;
